import { useState, useEffect, lazy, Suspense } from "react";
import HeroSection from "./components/HeroSection";
import MichiganCommunitiesWeServe from "./components/CommunitiesWeServe";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";

const CitiesDirectory = lazy(() => import("./components/CitiesDirectory"));
const StateResources = lazy(() => import("./components/StateResources"));
const FAQSection = lazy(() => import("./components/FAQSection"));
const FinalCTA = lazy(() => import("./components/FinalCTA"));

export default function MichiganPage() {
  useEffect(() => {
    document.title =
      "Expedited & Emergency Passport Services Michigan | Same-Day MI Service | Fast Passport Center";

    const desc = document.querySelector('meta[name="description"]');
    if (desc) {
      desc.setAttribute(
        "content",
        "Fast Passport Center provides emergency, expedited, and same-day passport services across all Michigan cities. Serving Detroit, Grand Rapids, Ann Arbor, Lansing, and beyond. Regional agency in Detroit. Courier service covers all of Michigan including the Upper Peninsula."
      );
    }

    const keywords = document.querySelector('meta[name="keywords"]');
    if (keywords) {
      keywords.setAttribute(
        "content",
        "expedited passport Michigan, emergency passport Michigan, same day passport MI, fast passport Detroit, passport renewal Grand Rapids, passport service Ann Arbor"
      );
    }

    const geoRegion = document.querySelector('meta[name="geo.region"]');
    if (geoRegion) geoRegion.setAttribute("content", "US-MI");
    const geoPlacename = document.querySelector('meta[name="geo.placename"]');
    if (geoPlacename) geoPlacename.setAttribute("content", "Michigan");
    const geoPos = document.querySelector('meta[name="geo.position"]');
    if (geoPos) geoPos.setAttribute("content", "44.3148;-85.6024");
    const icbm = document.querySelector('meta[name="ICBM"]');
    if (icbm) icbm.setAttribute("content", "44.3148, -85.6024");

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical)
      canonical.setAttribute("href", "https://fastpassportcenter.com/michigan");

    let schema = document.getElementById("mi-schema");
    if (!schema) {
      schema = document.createElement("script");
      schema.id = "mi-schema";
      schema.type = "application/ld+json";
      document.head.appendChild(schema);
    }

    schema.textContent = JSON.stringify([
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: "https://fastpassportcenter.com/",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "Michigan",
            item: "https://fastpassportcenter.com/michigan",
          },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        name: "Fast Passport Center - Michigan",
        description:
          "Expedited and emergency passport services across Michigan — Detroit, Grand Rapids, Ann Arbor, Lansing, and every city in between including the Upper Peninsula.",
        url: "https://fastpassportcenter.com/michigan",
        telephone: "+1-877-253-0084",
        email: "info@fastpassportcenter.com",
        priceRange: "$$",
        areaServed: {
          "@type": "State",
          name: "Michigan",
        },
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: "4.9",
          reviewCount: "892",
        },
        openingHoursSpecification: [
          {
            "@type": "OpeningHoursSpecification",
            dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
            opens: "08:00",
            closes: "20:00",
          },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "Service",
        name: "Emergency Passport Service Michigan",
        description:
          "Same-day and next-day emergency passport processing for Michigan travelers with urgent international travel needs.",
        provider: { "@type": "LocalBusiness", name: "Fast Passport Center" },
        areaServed: { "@type": "State", name: "Michigan" },
      },
      {
        "@context": "https://schema.org",
        "@type": "Service",
        name: "Expedited Passport Renewal Michigan",
        description:
          "2–3 week expedited passport renewal service for Michigan residents.",
        provider: { "@type": "LocalBusiness", name: "Fast Passport Center" },
        areaServed: { "@type": "State", name: "Michigan" },
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: [
          {
            "@type": "Question",
            name: "Does Michigan have its own passport agency?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Michigan has one regional passport agency: the Detroit Passport Agency (211 W. Fort St, Suite 1300, Detroit, MI 48226). It requires appointments and serves travelers with confirmed travel within 14 days. Upper Peninsula residents (8+ hours from Detroit) especially benefit from Fast Passport Center's courier service.",
            },
          },
          {
            "@type": "Question",
            name: "How fast can I get a passport in Michigan?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "With Fast Passport Center, Michigan residents can get same-day emergency service or 2–3 week expedited processing. Routine service through the post office takes 6–13 weeks plus mailing time.",
            },
          },
          {
            "@type": "Question",
            name: "Do I need a passport for a Canada trip from Michigan?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "A valid passport is required for all air travel to Canada. For land border crossings via the Ambassador Bridge, Blue Water Bridge, Detroit Windsor Tunnel, or Sault Ste. Marie, U.S. citizens can use a passport card, enhanced driver's license, or Trusted Traveler card. A passport book is recommended for maximum flexibility.",
            },
          },
          {
            "@type": "Question",
            name: "How do Upper Peninsula residents get expedited passports?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "The Upper Peninsula is 8+ hours from the Detroit Passport Agency. For UP residents with urgent travel needs, Fast Passport Center's courier service is the fastest option — we hand-carry your application, eliminating the need for you to travel. Same-day and next-day service is available.",
            },
          },
        ],
      },
    ]);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <HeroSection />
      <Suspense
        fallback={
          <div className="h-64 flex items-center justify-center bg-gray-50">
            <div className="animate-pulse text-gray-400 text-sm">Loading cities...</div>
          </div>
        }
      >
        <CitiesDirectory />
      </Suspense>
      <MichiganCommunitiesWeServe />
      <Suspense
        fallback={
          <div className="h-64 flex items-center justify-center bg-white">
            <div className="animate-pulse text-gray-400 text-sm">Loading resources...</div>
          </div>
        }
      >
        <StateResources />
      </Suspense>

      {/* How We Serve Michigan */}
      <section className="bg-[#F7F8FA] py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-14">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">
              Passport Services
            </p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
              How We Serve Michigan
            </h2>
            <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
              From the auto corridors of Detroit to the lake shores of Traverse City, from the university halls of Ann Arbor to the remote Upper Peninsula — we help Michigan travelers get their passports fast. One regional agency in Detroit serves the entire state, which is why our courier service is so valuable.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: "ri-flight-takeoff-line",
                title: "Emergency Same-Day Service",
                desc: "For Michigan travelers with flights within 72 hours. We hand-carry your application to the Detroit Passport Agency or Chicago — no road trip required.",
              },
              {
                icon: "ri-time-line",
                title: "Expedited 2–3 Week Service",
                desc: "Standard expedited processing with overnight shipping and tracking for Michigan residents. Perfect for upcoming summer or winter travel.",
              },
              {
                icon: "ri-file-list-3-line",
                title: "New Passport Applications",
                desc: "First-time applicants in Michigan — we handle document prep, photos, and acceptance facility scheduling. Especially helpful in rural Michigan.",
              },
              {
                icon: "ri-refresh-line",
                title: "Passport Renewals",
                desc: "Michigan renewals by mail or courier. We ensure your application is complete to avoid delays — critical before your winter Caribbean trip.",
              },
              {
                icon: "ri-flag-line",
                title: "Canada Border Crossing",
                desc: "Michigan borders Canada on three sides. We help residents near the Ambassador Bridge, Blue Water Bridge, and Sault Ste. Marie get proper travel documents.",
              },
              {
                icon: "ri-error-warning-line",
                title: "Lost, Stolen & Damaged",
                desc: "Fast replacement for Michigan residents who need a new passport quickly — especially for those traveling across the Canadian border.",
              },
            ].map((svc, i) => (
              <div
                key={i}
                className="bg-white rounded-lg p-7 border border-gray-100 hover:border-[#F6C344]/40 transition-all duration-200"
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#EEF2F8] mb-5">
                  <i className={`${svc.icon} text-[#1A365D] text-xl`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-base mb-2">{svc.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{svc.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Suspense
        fallback={
          <div className="h-64 flex items-center justify-center bg-gray-50">
            <div className="animate-pulse text-gray-400 text-sm">Loading FAQs...</div>
          </div>
        }
      >
        <FAQSection />
      </Suspense>

      <Suspense
        fallback={
          <div className="h-64 flex items-center justify-center bg-white">
            <div className="animate-pulse text-gray-400 text-sm">Loading CTA...</div>
          </div>
        }
      >
        <FinalCTA />
      </Suspense>

      <section className="bg-[#F7F8FA] py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <SEOCrossLinks state="MI" showCities={false} />
        </div>
      </section>
    </div>
  );
}